import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Activity, Boxes, Check } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area, Cell,
} from "recharts";
import { toast } from "sonner";
import { BRAND_CHART_COLORS } from "@/lib/brand";
import { useConfirm } from "@/components/ConfirmDialog";
import AssignmentCard from "@/components/AssignmentCard";
import { useAuth } from "@/contexts/AuthContext";
import { useSchemas } from "@/contexts/SchemaContext";
import { downloadCsv, estimateMinutes } from "@/lib/schema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "");

export default function HeadsetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [headset, setHeadset] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [allSims, setAllSims] = useState([]);
  const [stats, setStats] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [progress, setProgress] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ target_minutes_per_day: 30, program_weeks: 6, program_start_date: "" });
  const [open, setOpen] = useState(false);
  const [chooseSim, setChooseSim] = useState("");
  const [ticked, setTicked] = useState([]);
  const confirmDialog = useConfirm();
  const { user } = useAuth();
  const { modules: mdata } = useSchemas();

  const load = async () => {
    try {
      const [h, a, s, st, t] = await Promise.all([
        api.get(`/headsets/${id}`),
        api.get(`/headsets/${id}/assignments`),
        api.get(`/simulations`),
        api.get(`/telemetry/stats/${id}?days=14`),
        api.get(`/telemetry/recent?limit=30&headset_id=${id}`),
      ]);
      setHeadset(h.data);
      setAssignments(a.data);
      setAllSims(s.data);
      setStats(st.data);
      setTelemetry(t.data);
      // Fetch patient progress if a patient is assigned
      if (h.data?.patient_id) {
        try {
          const pr = await api.get(`/patients/${h.data.patient_id}/progress`);
          setProgress(pr.data);
        } catch (_e) { setProgress(null); }
      } else {
        setProgress(null);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [id]);

  const saveApp = async () => {
    const current = assignments.filter((a) => a.simulation_id === chooseSim);
    const modOf = (a) => a.config?.ModuleId || a.module_id || "";
    const remove = current.filter((a) => !ticked.includes(modOf(a)));
    if (remove.length) {
      const ok = await confirmDialog({
        title: "Take activities away?",
        description: `${remove.length} activit${remove.length > 1 ? "ies" : "y"} will no longer be allowed on this headset and their saved settings are deleted.`,
        confirmText: "Remove", destructive: true,
      });
      if (!ok) return;
    }
    try {
      for (const mid of ticked.filter((m) => !current.some((a) => modOf(a) === m))) {
        await api.post(`/headsets/${id}/assignments`, { simulation_id: chooseSim, module_id: mid || undefined, config: {} });
      }
      for (const a of remove) await api.delete(`/assignments/${a.id}`);
      toast.success("Activities updated");
      setOpen(false); setChooseSim("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeAssignment = async (assignmentId, simName) => {
    const ok = await confirmDialog({
      title: "Remove module?",
      description: `"${simName || "This simulation"}" will no longer be available on this headset.`,
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/assignments/${assignmentId}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!headset) return <div className="text-slate-400 text-sm">Loading…</div>;

  // An app (simulation) can contain several activities. The app asks the platform on launch which ones are allowed.
  const modIdOf = (a) => a.config?.ModuleId || a.module_id || "";
  const appModules = (sim) => (sim.module_ids?.length ? sim.module_ids : (sim.module_id ? [sim.module_id] : [""]));
  const moduleOf = (mid) => (mdata?.modules || []).find((m) => m.id === mid);
  const chosenSim = allSims.find((x) => x.id === chooseSim);
  const assignedNow = chosenSim ? assignments.filter((a) => a.simulation_id === chosenSim.id).map(modIdOf) : [];
  const appChanged = !!chosenSim && (ticked.some((m) => !assignedNow.includes(m)) || assignedNow.some((m) => !ticked.includes(m)));
  const pickApp = (sim) => { setChooseSim(sim.id); setTicked(assignments.filter((a) => a.simulation_id === sim.id).map(modIdOf)); };
  const sortedAssignments = [...assignments].sort((a, b) => a.simulation_id.localeCompare(b.simulation_id) || modIdOf(a).localeCompare(modIdOf(b)));
  const target = headset.patient?.target_minutes_per_day;
  const planned = assignments.map((a) => {
    let extra = {};
    try { extra = JSON.parse(a.config?.Extra || "{}"); } catch (_e) { /* ignore */ }
    return a.config?.Enabled === false ? null : estimateMinutes(moduleOf(modIdOf(a)), Number(a.config?.Level) || 1, extra);
  });
  const plannedTotal = Math.round(planned.reduce((t, m) => t + (m || 0), 0) * 10) / 10;
  const unknownTimes = planned.filter((m, i) => m == null && assignments[i].config?.Enabled !== false).length;

  const openPlanEditor = () => {
    setPlanForm({
      target_minutes_per_day: progress?.target_minutes_per_day || 30,
      program_weeks: progress?.program_weeks || 6,
      program_start_date: progress?.program_start_date || new Date().toISOString().slice(0, 10),
    });
    setPlanOpen(true);
  };

  const savePlan = async () => {
    if (!headset.patient_id) return;
    try {
      await api.patch(`/patients/${headset.patient_id}`, {
        target_minutes_per_day: parseInt(planForm.target_minutes_per_day || 30, 10),
        program_weeks: parseInt(planForm.program_weeks || 6, 10),
        program_start_date: planForm.program_start_date,
      });
      toast.success("Treatment plan updated");
      setPlanOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-purple-700 inline-flex items-center gap-1 mb-4 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-8 pb-6 border-b border-slate-200/60">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-purple-700 mb-2">Headset</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-mono">
            {headset.name || headset.serial}
          </h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Badge variant="outline" className="font-mono text-[10px] chip-brand border-0">{headset.model}</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px] uppercase tracking-[0.12em] font-semibold">
              {headset.status}
            </Badge>
            {headset.test && <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[10px] uppercase tracking-[0.12em] font-semibold" data-testid="test-tag">test</Badge>}
            {user?.role === "admin" && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!headset.test} data-testid="headset-test-toggle"
                  onChange={async (e) => {
                    try { await api.patch(`/headsets/${id}`, { test: e.target.checked }); load(); }
                    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }} /> Test headset
              </label>
            )}
            {headset.patient && (
              <span className="text-sm text-slate-600">Patient: <span className="font-medium text-slate-900">{headset.patient.name}</span></span>
            )}
            {headset.patient_id && (
              <Button size="sm" variant="outline" className="border-purple-200 hover:bg-purple-50" data-testid="view-recovery"
                onClick={() => navigate(`/patients/${headset.patient_id}`)}>
                View recovery
              </Button>
            )}
            <button type="button" className="text-xs text-slate-500 hover:text-purple-700 underline" data-testid="export-csv-headset"
              onClick={() => downloadCsv(api, { headset_id: id, days: 365 }, `${headset.name || headset.serial}-data.csv`).catch(() => toast.error("Export failed"))}>
              Export data (CSV)
            </button>
            {headset.last_seen && (
              <span className="text-xs text-slate-500 tabular-nums">Last sync · {new Date(headset.last_seen).toLocaleString()}</span>
            )}
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Kpi label="Sessions (14d)" value={stats?.daily?.reduce((s, d) => s + d.sessions, 0) || 0} />
        <Kpi label="Total minutes" value={(stats?.daily?.reduce((s, d) => s + d.duration_minutes, 0) || 0).toFixed(0)} />
        <Kpi label="Avg completion" value={avgPct(stats?.daily) + "%"} />
        <Kpi label="Modules assigned" value={assignments.length} accent />
      </div>

      {/* Patient Treatment Plan + Progress */}
      {headset.patient_id && progress && (
        <ProgressCard
          progress={progress}
          patient={headset.patient}
          onEdit={openPlanEditor}
        />
      )}

      {/* Treatment plan editor */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Treatment plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Daily target (minutes)</Label>
              <Input
                type="number"
                min={5}
                max={240}
                value={planForm.target_minutes_per_day}
                onChange={(e) => setPlanForm({ ...planForm, target_minutes_per_day: e.target.value })}
                data-testid="plan-target-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Program length (weeks)</Label>
              <Input
                type="number"
                min={1}
                max={52}
                value={planForm.program_weeks}
                onChange={(e) => setPlanForm({ ...planForm, program_weeks: e.target.value })}
                data-testid="plan-weeks-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Start date</Label>
              <Input
                type="date"
                value={planForm.program_start_date}
                onChange={(e) => setPlanForm({ ...planForm, program_start_date: e.target.value })}
                data-testid="plan-start-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlanOpen(false)}>Cancel</Button>
            <Button data-testid="plan-save-button" onClick={savePlan} className="btn-brand rounded-full">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-8 panel rounded-2xl p-6">
          <Tabs defaultValue="usage">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700">Patient Usage</div>
                <h3 className="font-display text-lg font-semibold text-slate-900">Recovery activity · last 14 days</h3>
              </div>
              <TabsList className="bg-purple-50">
                <TabsTrigger value="usage" data-testid="tab-usage">Usage</TabsTrigger>
                <TabsTrigger value="duration" data-testid="tab-duration">Duration</TabsTrigger>
                <TabsTrigger value="per-sim" data-testid="tab-per-sim">Per Module</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="usage">
              <div className="h-72" data-testid="usage-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats?.daily || []}>
                    <defs>
                      <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BRAND_CHART_COLORS.purple} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={BRAND_CHART_COLORS.cyan} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9d5ff" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9d5ff" }} />
                    <Area type="monotone" dataKey="sessions" stroke={BRAND_CHART_COLORS.purple} fill="url(#gs)" strokeWidth={2.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            <TabsContent value="duration">
              <div className="h-72" data-testid="duration-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats?.daily || []}>
                    <defs>
                      <linearGradient id="gbar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BRAND_CHART_COLORS.cyan} />
                        <stop offset="100%" stopColor={BRAND_CHART_COLORS.purple} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9d5ff" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9d5ff" }} />
                    <Bar dataKey="duration_minutes" fill="url(#gbar)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            <TabsContent value="per-sim">
              <div className="h-72" data-testid="per-sim-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats?.per_simulation || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9d5ff" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9d5ff" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="duration_minutes" name="Minutes" fill={BRAND_CHART_COLORS.purple} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="avg_completion" name="Completion %" fill={BRAND_CHART_COLORS.cyan} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-4 panel rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700 mb-3 flex items-center gap-2">
            <Activity className="h-3 w-3" /> Live telemetry
          </div>
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Recent activity</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="telemetry-stream-list">
            {telemetry.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No events</div>
            ) : telemetry.map((e) => (
              <div key={e.id} className="border-l-2 border-purple-400 pl-3 py-1">
                <div className="text-xs font-mono text-slate-700">{e.event_type}</div>
                <div className="text-[10px] text-slate-500 tabular-nums">{new Date(e.created_at).toLocaleString()}</div>
                {e.duration_seconds ? <div className="text-[10px] text-slate-500">{Math.round(e.duration_seconds / 60)} min · {e.completion_pct}%</div> : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Simulations + configs */}
      <div className="panel rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700">Therapy Plan</div>
            <h3 className="font-display text-xl font-semibold text-slate-900">Treatment plan: activities on this headset</h3>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setChooseSim(""); }}>
            <DialogTrigger asChild>
              <Button data-testid="assign-simulation-button" disabled={allSims.length === 0} className="btn-brand rounded-full">
                <Plus className="h-4 w-4 mr-2" /> Add or change activities
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">Assign simulation</DialogTitle>
              </DialogHeader>
              <div className="mt-2">
                <div className="text-xs text-slate-500 mb-3">
                  Choose a module — its icon and category show what it&apos;s for.
                </div>
                <p className="text-sm text-slate-600 mb-3">Pick an app, then tick the activities inside it that this patient may use. When the app starts it asks the platform what is allowed and only shows the ticked ones.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[30vh] overflow-y-auto pr-1" data-testid="sim-picker-grid">
                  {allSims.map((sim) => {
                    const selected = chooseSim === sim.id;
                    const names = appModules(sim).filter(Boolean).map((mid) => moduleOf(mid)?.name || mid);
                    const allowed = assignments.filter((x) => x.simulation_id === sim.id).length;
                    return (
                      <button key={sim.id} type="button" onClick={() => pickApp(sim)} data-testid={`sim-pick-${sim.id}`}
                        className={`text-left rounded-xl border transition-all p-3 flex items-center gap-3 ${selected ? "sim-card-selected shadow-lg shadow-purple-500/20" : "bg-white border-slate-200 hover:border-purple-300 hover:bg-purple-50/40"}`}>
                        <div className={`relative h-14 w-14 rounded-lg overflow-hidden flex-shrink-0 ${selected ? "bg-white/15" : "bg-slate-100 border border-slate-200"}`}>
                          {sim.image_url ? <img src={imgSrc(sim.image_url)} alt={sim.name} className="h-full w-full object-cover" />
                            : <div className="h-full w-full grid place-items-center"><Boxes className={`h-5 w-5 ${selected ? "text-white/80" : "text-slate-400"}`} strokeWidth={1.5} /></div>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`font-medium truncate ${selected ? "text-white" : "text-slate-900"}`}>{sim.name}</div>
                          <div className={`text-[11px] mt-0.5 ${selected ? "text-white/85" : "text-slate-500"}`}>
                            {names.length ? `${names.length} ${names.length === 1 ? "activity" : "activities"}: ${names.join(", ")}` : "Whole app (no separate activities)"}
                          </div>
                          <div className={`text-[10px] mt-0.5 ${selected ? "text-white/80" : "text-slate-400"}`}>{allowed} allowed on this headset</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {chosenSim && (
                  <div className="mt-4 border border-purple-100 rounded-xl p-3" data-testid="app-activities">
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-2">Activities in {chosenSim.name}</div>
                    {appModules(chosenSim).map((mid) => {
                      const m = moduleOf(mid);
                      return (
                        <label key={mid || "whole"} className="flex items-start gap-2 py-1.5 border-t border-slate-100 first:border-t-0 text-sm">
                          <input type="checkbox" className="mt-1" checked={ticked.includes(mid)} data-testid={`allow-${mid || "whole"}`}
                            onChange={() => setTicked(ticked.includes(mid) ? ticked.filter((x) => x !== mid) : [...ticked, mid])} />
                          <span>
                            <span className="font-medium text-slate-900">{m ? m.name : (mid || "Whole app")}</span>
                            {m?.purpose && <span className="block text-xs text-slate-500 line-clamp-2">{m.purpose}</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setChooseSim(""); }}>Cancel</Button>
                <Button data-testid="assign-sim-submit" onClick={saveApp} disabled={!appChanged} className="btn-brand rounded-full">
                  Save activities
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {assignments.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500">No simulations assigned to this headset yet.</div>
        ) : (
          <>
          {target && plannedTotal > target && (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="plan-conflict">
              <b>Time conflict.</b> At their current levels these activities take about <b>{plannedTotal} min</b> if the patient does each one once,
              but this patient's daily target is <b>{target} min</b>.{unknownTimes ? ` (${unknownTimes} activit${unknownTimes > 1 ? "ies have" : "y has"} no timing data and ${unknownTimes > 1 ? "are" : "is"} not counted.)` : ""}
              {" "}Lower a level, shorten the rounds, remove an activity, or change the daily target on the patient page.
            </div>
          )}
          <Accordion type="single" collapsible className="space-y-2">
            {sortedAssignments.map((a, i) => {
              const sim = a.simulation || allSims.find((x) => x.id === a.simulation_id);
              const first = i === 0 || sortedAssignments[i - 1].simulation_id !== a.simulation_id;
              const total = sim ? appModules(sim).filter(Boolean).length : 0;
              const here = sortedAssignments.filter((x) => x.simulation_id === a.simulation_id).length;
              return (
                <div key={a.id}>
                  {first && (
                    <div className="flex items-center justify-between pt-3 pb-1 text-xs text-slate-600" data-testid={`app-header-${a.simulation_id}`}>
                      <span><b className="text-slate-900">{sim?.name || "App"}</b>{total > 1 ? ` · ${here} of ${total} activities allowed` : ""}</span>
                      {total > 1 && <button type="button" className="text-purple-700 hover:underline" onClick={() => { if (sim) { pickApp(sim); setOpen(true); } }}>Allow or remove activities</button>}
                    </div>
                  )}
                  <AssignmentCard assignment={a} simulation={sim} imgSrc={imgSrc} onRemove={removeAssignment} onChanged={load}
                    patientId={headset.patient_id} targetMinutes={target} />
                </div>
              );
            })}
          </Accordion>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`panel rounded-2xl p-5 ${accent ? "relative overflow-hidden" : ""}`}>
      {accent && <div className="absolute inset-0 bg-brand-gradient opacity-10 pointer-events-none" />}
      <div className="relative text-[10px] uppercase tracking-[0.18em] text-purple-700 mb-2">{label}</div>
      <div className="relative font-display text-3xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function ProgressCard({ progress, patient, onEdit }) {
  const STATUS_COLORS = {
    on_track: "hsl(160 60% 45%)",   // green
    over: BRAND_CHART_COLORS.purple,
    behind: "hsl(35 90% 55%)",       // amber
    missed: "hsl(0 70% 60%)",        // red
  };
  const last14 = progress.daily.slice(-14);

  return (
    <div className="panel rounded-2xl p-6 mb-6" data-testid="patient-progress-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700">Treatment Adherence</div>
          <h3 className="font-display text-xl font-semibold text-slate-900">
            {patient?.name ? `${patient.name}'s recovery plan` : "Recovery plan"}
          </h3>
          <div className="text-xs text-slate-500 mt-1 tabular-nums">
            {progress.target_minutes_per_day} min/day · {progress.program_weeks} weeks ·
            {" "}starts {progress.program_start_date} → ends {progress.program_end_date}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit} className="border-purple-200 hover:bg-purple-50" data-testid="edit-plan-button">
          Edit plan
        </Button>
      </div>

      {/* Adherence ring + stats */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <div className="md:col-span-3 flex items-center gap-4">
          <AdherenceRing pct={progress.adherence_pct} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Adherence</div>
            <div className="font-display text-3xl font-bold text-slate-900 tabular-nums">{progress.adherence_pct}%</div>
            <div className="text-[11px] text-slate-500 tabular-nums">
              {progress.days_elapsed} of {progress.days_elapsed + progress.days_remaining} days · {progress.days_remaining} left
            </div>
          </div>
        </div>

        <div className="md:col-span-5 grid grid-cols-2 gap-3 content-start">
          <ProgressStat label="On track" value={progress.days_on_track} color={STATUS_COLORS.on_track} />
          <ProgressStat label="Behind" value={progress.days_behind} color={STATUS_COLORS.behind} />
          <ProgressStat label="Missed" value={progress.days_missed} color={STATUS_COLORS.missed} />
          <ProgressStat label="At 100% completion" value={progress.days_at_100_completion} color={BRAND_CHART_COLORS.purple} />
        </div>

        <div className="md:col-span-4">
          {progress.suggest_increase_difficulty ? (
            <div className="rounded-xl p-4 border bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-200" data-testid="difficulty-hint">
              <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700 mb-1">Clinical hint</div>
              <div className="text-sm text-slate-800 font-medium">Consider increasing difficulty</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                Patient is averaging ≥95% completion over the last 7 days. The current plan may be too easy.
              </div>
            </div>
          ) : progress.days_behind + progress.days_missed > progress.days_on_track + progress.days_over ? (
            <div className="rounded-xl p-4 border bg-amber-50 border-amber-200">
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 mb-1">Heads up</div>
              <div className="text-sm text-slate-800 font-medium">Patient is behind schedule</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                More days have been missed or behind than on-track. Consider reducing daily target or following up.
              </div>
            </div>
          ) : (
            <div className="rounded-xl p-4 border bg-emerald-50 border-emerald-200">
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 mb-1">Status</div>
              <div className="text-sm text-slate-800 font-medium">Patient is keeping pace</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                Recovery plan is progressing within expected parameters.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Daily timeline */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Last {last14.length} days · target vs actual minutes</div>
        <div className="h-44" data-testid="adherence-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={last14}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9d5ff" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9d5ff" }}
                formatter={(v, n) => [`${v} min`, n === "target_minutes" ? "Target" : "Actual"]}
              />
              <Bar dataKey="target_minutes" fill="#e9d5ff" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual_minutes" radius={[4, 4, 0, 0]}>
                {last14.map((d, i) => (
                  <Cell key={i} fill={STATUS_COLORS[d.status] || BRAND_CHART_COLORS.purple} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-4 text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-2 flex-wrap">
          <LegendDot color={STATUS_COLORS.on_track} label="On track" />
          <LegendDot color={STATUS_COLORS.over} label="Over target" />
          <LegendDot color={STATUS_COLORS.behind} label="Behind" />
          <LegendDot color={STATUS_COLORS.missed} label="Missed" />
          <LegendDot color="#e9d5ff" label="Target" />
        </div>
      </div>
    </div>
  );
}

function AdherenceRing({ pct }) {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.min(100, Math.max(0, pct)) / 100) * circumference;
  return (
    <svg width="80" height="80" className="-rotate-90">
      <defs>
        <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BRAND_CHART_COLORS.cyan} />
          <stop offset="100%" stopColor={BRAND_CHART_COLORS.purple} />
        </linearGradient>
      </defs>
      <circle cx="40" cy="40" r={radius} stroke="#e9d5ff" strokeWidth="8" fill="none" />
      <circle cx="40" cy="40" r={radius} stroke="url(#ringGrad)" strokeWidth="8" fill="none"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circumference - dash}`} />
    </svg>
  );
}

function ProgressStat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-purple-100 bg-white/70 p-3">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function avgPct(daily) {
  if (!daily || daily.length === 0) return 0;
  const vals = daily.map((d) => d.avg_completion).filter((v) => v > 0);
  if (!vals.length) return 0;
  return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
}
